import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Logo } from "@/components/layout/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ArrowRight, CheckCircle2, XCircle, RefreshCw, KeyRound } from "lucide-react";

const RULES = [
  { key: "len", label: "At least 8 characters", test: (p) => p.length >= 8 },
  { key: "upper", label: "One uppercase letter", test: (p) => /[A-Z]/.test(p) },
  { key: "lower", label: "One lowercase letter", test: (p) => /[a-z]/.test(p) },
  { key: "digit", label: "One number", test: (p) => /\d/.test(p) },
  { key: "special", label: "One special character", test: (p) => /[^A-Za-z0-9]/.test(p) },
];

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const passed = RULES.map((r) => ({ ...r, ok: r.test(password) }));
  const allOk = passed.every((r) => r.ok);
  const match = password && password === confirm;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!allOk) { setError("Please meet all password requirements."); return; }
    if (!match) { setError("Passwords do not match."); return; }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      toast.success("Password reset! Please log in.");
      navigate("/login");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Reset failed");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:flex flex-col justify-between bg-navy text-white p-12 relative overflow-hidden">
        <img src="https://images.unsplash.com/photo-1562774053-701939374585?w=1200&q=80" alt="" className="absolute inset-0 h-full w-full object-cover opacity-25" />
        <div className="absolute inset-0 bg-gradient-to-br from-navy-deep to-navy/60" />
        <div className="relative"><Logo light /></div>
        <div className="relative">
          <h2 className="headline text-5xl">Choose a new <span className="text-orange">password</span></h2>
          <p className="text-white/70 mt-4 max-w-md">Pick something strong and memorable — you'll use it to sign in from now on.</p>
        </div>
        <div className="relative text-white/40 text-xs">© 2026 Elite by Thotis Media</div>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-12 bg-background">
        <div className="w-full max-w-sm" data-testid="reset-form">
          <div className="lg:hidden mb-8"><Logo /></div>
          <div className="eyebrow text-xs text-orange mb-2">Set new password</div>
          <h1 className="headline text-4xl text-navy mb-6">New password</h1>

          {!token ? (
            <div className="text-center py-8">
              <XCircle className="h-12 w-12 text-destructive mx-auto mb-3" />
              <p className="text-muted-foreground">This reset link is invalid. Please request a new one.</p>
              <Link to="/forgot-password" className="inline-block mt-4 font-bold text-navy hover:text-orange">Request a new link</Link>
            </div>
          ) : (
            <form onSubmit={submit}>
              {error && <div className="mb-4 text-sm text-destructive bg-destructive/10 px-4 py-3 rounded-xl" data-testid="reset-error">{error}</div>}
              <div className="flex items-center gap-3 p-4 rounded-2xl bg-navy/5 mb-4">
                <KeyRound className="h-5 w-5 text-navy" />
                <p className="text-sm text-navy">This link works only once and expires in 30 minutes.</p>
              </div>
              <div>
                <Label htmlFor="password">New password</Label>
                <Input id="password" data-testid="reset-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="mt-1.5 h-12 rounded-xl" placeholder="••••••••" />
              </div>
              <ul className="mt-3 space-y-1.5">
                {passed.map((r) => (
                  <li key={r.key} className={`flex items-center gap-2 text-sm ${r.ok ? "text-green-600" : "text-muted-foreground"}`}>
                    {r.ok ? <CheckCircle2 className="h-4 w-4" /> : <XCircle className="h-4 w-4 opacity-40" />} {r.label}
                  </li>
                ))}
              </ul>
              <div className="mt-4">
                <Label htmlFor="confirm">Confirm password</Label>
                <Input id="confirm" data-testid="reset-confirm" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} className="mt-1.5 h-12 rounded-xl" placeholder="••••••••" />
                {confirm && !match && <p className="text-xs text-destructive mt-1">Passwords do not match</p>}
              </div>
              <Button type="submit" data-testid="reset-submit" disabled={loading || !allOk || !match} className="w-full mt-6 h-12 rounded-full bg-orange hover:bg-orange-dark text-white font-display font-bold italic uppercase tracking-wide">
                {loading ? <><RefreshCw className="h-4 w-4 mr-2 animate-spin" /> Saving…</> : <>Reset password <ArrowRight className="h-4 w-4 ml-1" /></>}
              </Button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
